import { PropsWithChildren, useEffect, useMemo, useState } from "react";
import { Texture, Resource } from "pixi.js";
import { ZoomInMap, ZoomOutMap } from "@mui/icons-material";
import { useSelector } from "../../../../../app/helpers";
import { RootState } from "../../../../../app/rootReducer";
import { DisplayViewport } from "../../DisplayViewport/DisplayViewport";
import { FloorPlanBackground } from "../../FloorPlanBackground/FloorPlanBackground";
import { ZoneContainer } from "../../Zone/ZoneContainer";
import { PlaceContainer } from "../../Place/PlaceContainer";
import { IFloorPayload } from "../../../Domain/Types/FloorPlan/FloorPayload.type";
import { ISeatStatus } from "../../../Domain/Types/FloorPlan/SeatStatus.type";
import { generatePlaceVariant } from "../../../Functions/generateVariant";
import { CapacityObject } from "../../../../../features/Reports/typings/reports.types";
import { useSelection } from "../../../Hooks/useSelection/useSelection";
import { useGeometry } from "../../../Hooks/useGeometry";
import { IViewport } from "../../../Domain/Types/FloorPlan/Viewport.type";
import { BookingType } from "../../../../../features/Booking-Form/typings/booking-inputs";
import { IPlaceSchedule } from "../../../Domain/Types/FloorPlan/PlaceSchedule";
import {
  generateColorOverlay,
  renderReportPlaceBySelected,
  renderReportZoneBySelected
} from "./ReportFloorPlanView.functions";
import { PlanViewMode } from "../../PixiViewport/PixiViewport";
import { IconContainer } from "../../Canvas/Partials/jump-icon.component";
import { CurrentFloorInfo } from "../../../../Svg/functions/svg-dialog.functions";
import {
  SvgReportChipGroup,
  SvgReportChipGroupProps
} from "../../../../Svg/SvgReportChipGroup/SvgReportChipGroup.component";
import { EmptyBackgroundText } from "../../Canvas/Partials/EmptyBackgroundText.partial";
import {
  getFloorPlanCanvasCorner,
  resolveFloorPlanCanvasSize,
  resolveFloorPlanPlaceScale
} from "../../../Functions/floorPlanCanvas.functions";

interface Props {
  width: number;
  height: number;
  name: string;
  floorPlan: IFloorPayload;
  seatStatus: ISeatStatus;
  background: Texture<Resource>;
  isEmptyBgUsed: boolean;
  reportData: (CapacityObject | undefined)[] | undefined;
  /** show if chip is on in the frontend */
  isSelectedChip?: boolean;
  onClickReportPlace?: (inventoryIds: number[], names: string[], placeTypes: BookingType[]) => void;
  onClickReportZone?: (
    inventoryIds: number[],
    names: string[],
    zoneTypes: BookingType[],
    highlightedPlaces: IPlaceSchedule[]
  ) => void;
  currentFloorInfo?: CurrentFloorInfo;
  isFloorFullMode: { index: number | null; open: boolean };
  setIsFloorFullMode: (mode: { index: number | null; open: boolean }) => void;
  reportChipsValue: SvgReportChipGroupProps;
}

export function ReportFloorPlanView({
  height,
  children,
  name,
  background,
  isEmptyBgUsed,
  floorPlan,
  seatStatus,
  reportData,
  isSelectedChip,
  onClickReportPlace,
  onClickReportZone,
  currentFloorInfo,
  isFloorFullMode,
  setIsFloorFullMode,
  reportChipsValue
}: PropsWithChildren<Props>) {
  const { selectedZoneOrWorkplace, selectedByAttFilter } = useSelector(
    (state: RootState) => state.report
  );
  const { color: companyColor } = useSelector(
    (state: RootState) => state.login.userInformation.company.meta
  );
  const fullScreenIconColor = companyColor ?? "#00d084";

  const {
    selectZone,
    selectDesk,
    wipeSelection,
    wipeDeskSelection,
    wipeZoneSelection,
    selectedDesks,
    selectedZones
  } = useSelection();
  const { convertPixiPoints } = useGeometry();

  const [isSamePlaceState, setIsSamePlaceState] = useState<number[]>([]);
  const [isSameZoneState, setIsSameZoneState] = useState<number[]>([]);
  const [isAttFilterPlaceState, setIsAttFilterPlaceState] = useState<number[]>([]);
  const [isAttFilterZoneState, setIsAttFilterZoneState] = useState<number[]>([]);

  const [viewportDimensions, setViewportDimensions] = useState<IViewport>({ width: 0, height: 0 });
  const [placeScale, setPlaceScale] = useState(0);

  const canvasSize = useMemo(() => {
    if (!background || isEmptyBgUsed) {
      return floorPlan.viewport;
    }

    return resolveFloorPlanCanvasSize(floorPlan, background);
  }, [background, floorPlan, isEmptyBgUsed]);

  const highlightedPlace = floorPlan.places.filter(workplace =>
    selectedZones.includes(workplace.zoneScheduleId)
  );

  /* istanbul ignore next */
  function onClickZone(zoneId: number) {
    const selected = floorPlan.zones.find(zone => zone.id === zoneId);
    const alreadySelected = floorPlan.zones.filter(
      zone => selectedZones.length && selectedZones.includes(zone.id)
    );
    const selectedType = selected?.inventory?.zoneTypeId || selected?.inventory?.zoneType?.id;
    const alreadySelectedType = alreadySelected.map(
      s => s.inventory?.zoneTypeId || s.inventory?.zoneType?.id
    )[0];

    // during the multi selection if the type is different from the previous select, re-start multi select
    if (selectedType !== alreadySelectedType) wipeZoneSelection();

    selectZone(zoneId);
  }

  /* istanbul ignore next */
  function onClickPlace(deskId: number) {
    const selected = floorPlan.places.find(place => place.id === deskId);
    const alreadySelected = floorPlan.places.filter(
      place => selectedDesks.length && selectedDesks.includes(place.id)
    );
    const selectedType = selected?.inventory?.placeTypeId || selected?.inventory?.placeType?.id;
    const alreadySelectedType = alreadySelected.map(
      s => s.inventory?.placeTypeId || s.inventory?.placeType?.id
    )[0];

    // during the multi selection if the type is different from the previous select, re-start multi select
    if (selectedType !== alreadySelectedType) wipeDeskSelection();

    selectDesk(deskId);
  }

  /* istanbul ignore next */
  function handleClickFullScreen(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    event.preventDefault();

    if (isFloorFullMode.index === currentFloorInfo?.index)
      setIsFloorFullMode({ index: currentFloorInfo?.index ?? 0, open: !isFloorFullMode.open });
    // if different floor is selected, make full the selected floor
    else setIsFloorFullMode({ index: currentFloorInfo?.index ?? 0, open: true });
  }

  useEffect(() => {
    renderReportPlaceBySelected(floorPlan, selectedDesks, onClickReportPlace);
    renderReportZoneBySelected(floorPlan, selectedZones, highlightedPlace, onClickReportZone);
  }, [selectedDesks, selectedZones]);

  // set use-selection hooks only when the selected state is aligned with state
  // to avoid useSelection works at both floors when comparing two floors
  useEffect(() => {
    if (selectedZoneOrWorkplace?.open) {
      const selectedStateWps = floorPlan.places.filter(w =>
        selectedZoneOrWorkplace.id.includes(w.inventory?.id || 0)
      );
      const selectedStateZn = floorPlan.zones.filter(z =>
        selectedZoneOrWorkplace.id.includes(z.inventory?.id || 0)
      );

      if (selectedStateWps.length) setIsSamePlaceState(selectedStateWps.map(w => w.id));
      if (selectedStateZn.length) setIsSameZoneState(selectedStateZn.map(z => z.id));

      if (!selectedStateWps.length) setIsSamePlaceState([]);
      if (!selectedStateZn.length) setIsSameZoneState([]);
    }
  }, [selectedZoneOrWorkplace, floorPlan]);

  // only applied when booking group attributes are used
  useEffect(() => {
    if (!selectedByAttFilter?.some(sel => sel.open)) {
      setIsAttFilterPlaceState([]);
      setIsAttFilterPlaceState([]);
      setIsAttFilterZoneState([]);
      return;
    }

    const selectedZnOrWp = selectedByAttFilter.find(
      sel => sel.floorInventoryId === floorPlan.floorInventoryId
    );
    const selectedPl = floorPlan.places.filter(pl =>
      selectedZnOrWp?.id.includes(pl.inventory?.id || pl.inventoryId)
    );
    const selectedZn = floorPlan.zones.filter(zn =>
      selectedZnOrWp?.id.includes(zn.inventory?.id || zn.inventoryId)
    );

    if (selectedPl.length) setIsAttFilterPlaceState(selectedPl.map(w => w.id));
    if (selectedZn.length) setIsAttFilterZoneState(selectedZn.map(z => z.id));
    if (!selectedPl.length) setIsAttFilterPlaceState([]);
    if (!selectedZn.length) setIsAttFilterZoneState([]);
  }, [selectedByAttFilter, floorPlan]);

  useEffect(() => {
    if (!background) return;
    setPlaceScale(resolveFloorPlanPlaceScale(floorPlan, background));
  }, [background, floorPlan]);

  useEffect(() => {
    if (!isSelectedChip) {
      wipeSelection();
      setIsSamePlaceState([]);
      setIsSameZoneState([]);
    }
  }, [isSelectedChip]);

  return (
    <DisplayViewport
      planViewMode={PlanViewMode.REPORT}
      floorPlanName={name}
      fixedHeight={height}
      initialZoomEnd={getFloorPlanCanvasCorner(canvasSize)}
      currentFloorPlan={undefined}
      placeScale={placeScale}
      viewportDimensions={viewportDimensions}
      setViewportDimensions={setViewportDimensions}
      toolbars={
        <div
          style={{ position: "relative", zIndex: 1, height: "min-content" }}
          data-testid="report-floor-plan-toolbar"
        >
          <SvgReportChipGroup
            isFleetReport={false}
            workplaceBookingEnabled={reportChipsValue.workplaceBookingEnabled}
            name={reportChipsValue.name}
            averageUsers={reportChipsValue.averageUsers}
            numWorkplaces={reportChipsValue.numWorkplaces}
            numParkings={reportChipsValue.numParkings}
            numElectricchargings={reportChipsValue.numElectricchargings}
            numConferences={reportChipsValue.numConferences}
            avg={reportChipsValue.avg}
            dev={reportChipsValue.dev}
            currentFloorCapaUniqueIds={reportChipsValue.currentFloorCapaUniqueIds}
          />

          <div data-testid={"report-fullscreen-btn"}>
            <IconContainer
              icon={
                isFloorFullMode.open && isFloorFullMode.index === currentFloorInfo?.index
                  ? ZoomInMap
                  : ZoomOutMap
              }
              brandColor={fullScreenIconColor}
              top={"0px"}
              left={`${viewportDimensions.width - 60}px`}
              onClick={handleClickFullScreen}
            />
          </div>
        </div>
      }
    >
      {!isEmptyBgUsed && <FloorPlanBackground texture={background} floorPlan={floorPlan} />}
      {isEmptyBgUsed && (
        <EmptyBackgroundText
          rectWidth={canvasSize.width}
          rectHeight={canvasSize.height}
          posY={10}
        />
      )}

      {/** draw zones */}
      {floorPlan.zones.map(zone => (
        <ZoneContainer
          isClickable
          key={zone.id}
          id={zone.id}
          inventoryId={zone.inventoryId || zone.inventory?.id}
          onClick={onClickZone}
          walls={convertPixiPoints(zone.coordinates)}
          disabled={zone.disabled}
          zoneType={zone.zoneTypeId || zone.inventory?.zoneTypeId || 1}
          colorOverlay={generateColorOverlay({
            type: "zone",
            inventoryId: zone.inventoryId || zone.inventory?.id,
            reportData,
            floorPlan
          })}
          isSelected={[...isAttFilterZoneState, ...isSameZoneState].includes(zone.id)}
        />
      ))}

      {/** draw places */}
      {!!placeScale &&
        floorPlan.places.map(workplace => (
          <PlaceContainer
            isSelectable
            key={workplace.id}
            boundingBox={{
              width: workplace.inventory?.boundingBox?.width ?? 160,
              height: workplace.inventory?.boundingBox?.height ?? 80
            }}
            id={workplace.id}
            inventoryId={workplace.inventoryId || workplace.inventory?.id}
            rotation={workplace.rotate}
            position={workplace.position}
            variant={generatePlaceVariant(
              { workplace: workplace, zone: undefined },
              seatStatus,
              workplace.disabled
            )}
            colorOverlay={generateColorOverlay({
              type: "place",
              inventoryId: workplace.inventoryId || workplace.inventory?.id,
              reportData,
              floorPlan
            })}
            onClick={onClickPlace}
            placeTypeId={workplace.placeTypeId || workplace.inventory?.placeTypeId}
            placeScale={placeScale}
            isHighlighted={highlightedPlace.map(h => h.id).includes(workplace.id)}
            isSelected={[...isAttFilterPlaceState, ...isSamePlaceState].includes(workplace.id)}
            tool={"CAPA.REPORT"}
          />
        ))}
      {children}
    </DisplayViewport>
  );
}
